<script setup>
import {computed} from "vue";

const props = defineProps({
    chapters: {
        type: Array,
        required: true
    },
    courseId: {
        type: Number,
        required: true
    },
});

const emit = defineEmits(['edit-settings', 'delete:chapter']);

const deleteChapter = async (chapter) => {
    const res = await axios.delete(route('api.course.backoffice.chapter.destroy', {
        course_id: chapter.course_id,
        chapter_id: chapter.id
    }));

    if (res.data.success) {
        emit('delete:chapter', chapter);
    }
};

const toTimestamp = (value) => {
    const parsedValue = Date.parse(value);

    if (Number.isNaN(parsedValue)) {
        return 0;
    }

    return parsedValue;
};

const sortedChapters = computed(() => {
    const drafts = [];
    const publishedWithoutDate = [];
    const publishedWithDate = [];

    props.chapters.forEach((chapter) => {
        if (!chapter.published) {
            drafts.push(chapter);
            return;
        }

        if (chapter.available_from === null) {
            publishedWithoutDate.push(chapter);
            return;
        }

        publishedWithDate.push(chapter);
    });

    publishedWithDate.sort((chapterA, chapterB) => {
        return toTimestamp(chapterB.available_from) - toTimestamp(chapterA.available_from);
    });

    publishedWithoutDate.sort((chapterA, chapterB) => {
        return toTimestamp(chapterB.created_at) - toTimestamp(chapterA.created_at);
    });

    return [
        ...drafts,
        ...publishedWithoutDate,
        ...publishedWithDate,
    ];
});
</script>
<template>
    <ul id="chapter-ordered-list" class="mt-8 divide-y -mx-4">
        <li
            v-for="chapter in sortedChapters"
            :key="chapter.id"
            class="flex justify-between space-x-4 py-2 px-4 md:px-6 lg:px-8 hover:bg-gray-50 transition-all duration-150"
        >
            <div>
                <div>
                    <div class="flex items-center">
                        <span :class="! chapter.published ? 'opacity-70' : ''">
                            {{ chapter.title }}
                        </span>
                        <pill
                            v-if="! chapter.published"
                            size="xs"
                            style-type="info"
                            class="bg-gray-100 ml-1"
                        >
                            {{ _mft('course:chapter.status.draft') }}
                        </pill>
                    </div>
                </div>
                <div class="text-xs text-gray-500">
                    {{ chapter.available_from ? chapter.available_from_display : chapter.created_at_display }}
                </div>
                <div v-if="chapter.description" class="text-xs text-gray-500 whitespace-pre mt-1">
                    {{ chapter.description }}
                </div>
            </div>
            <div class="flex space-x-2">
                <div class="flex space-x-1">
                    <div class="flex space-x-1 items-start">
                        <button-more-options
                            size="sm"
                            :cancel-on-outside-click="false"
                            alignment="right"
                            style-type="transparent"
                        >
                            <button-more-options-option-with-confirmation
                                style-type="danger"
                                :confirm-text="_mft('shared:confirm.areYouSure')"
                                :confirm-button-label="_mft('shared:action.delete')"
                                :abort-button-label="_mft('shared:action.cancel')"
                                @confirmed="deleteChapter(chapter)"
                            >
                                <span class="text-xs">{{ _mft('course:chapter.delete') }}</span>
                            </button-more-options-option-with-confirmation>
                            <button-more-options-option size="sm" class="whitespace-nowrap">
                                <a
                                    :href="route('course.backoffice.chapter.preview', [courseId, chapter.id])"
                                    target="_blank"
                                    class="text-xs"
                                >
                                    {{ _mft('shared:previewInNewTab') }}
                                </a>
                            </button-more-options-option>
                        </button-more-options>

                        <button-base
                            size="sm"
                            @click="emit('edit-settings', chapter)"
                            style-type="transparent"
                        >
                            <i class="fa-regular fa-cog" />
                        </button-base>
                        <a :href="route('manageCourses.courses.chapters.pageBuilder', [courseId, chapter.id])">
                            <button-primary size="sm">
                                <i class="fa-regular fa-pencil" />
                            </button-primary>
                        </a>
                    </div>
                </div>
            </div>
        </li>
        <li class="flex justify-end py-2 px-4 md:px-6 lg:px-8 ">
            <a :href="route('course.backoffice.chapter.add', courseId)">
                <button-primary>
                    {{ _mft('course:addPost') }}
                </button-primary>
            </a>
        </li>
    </ul>
</template>
